/**
 * Sabah Aksiyon Bülteni — Morning Decision Report
 *
 * Her sabah 07:00'de Telegram'a aksiyon odaklı bülten gönderir.
 * İstatistik raporu değil, karar raporu: "bugün ne yap" listesi.
 */

import type { GrowBag, SensorReading, AlertEvent, DeviceStatus } from '../types';
import { SPECIES_INCUBATION_DAYS, SPECIES_FRUITING_WINDOW, getSpeciesById } from './thresholds';
import type { ContaminationRiskResult } from './contaminationRisk';
import type { HarvestPrediction } from '../hooks/useHarvestPredictionV2';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MorningBriefingData {
  date: string;
  todoItems: BriefingTodoItem[];
  warnings: string[];
  yesterdaySummary: {
    harvestGrams: number;
    activeBagCount: number;
    lostBagCount: number;
    healthScore: number;
  };
  deviceStatuses: { name: string; ok: boolean }[];
}

export interface BriefingTodoItem {
  emoji: string;
  bagName: string;
  action: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

// ── Briefing Generator ────────────────────────────────────────────────────────

export function generateMorningBriefing(opts: {
  bags: GrowBag[];
  yesterdayReadings: SensorReading[];
  alerts: AlertEvent[];
  devices: DeviceStatus[];
  healthScore: number;
  harvestPredictions: HarvestPrediction[];
  contaminationRisks: ContaminationRiskResult[];
}): MorningBriefingData {
  const { bags, yesterdayReadings, alerts, devices, healthScore, harvestPredictions, contaminationRisks } = opts;
  const now = new Date();
  const date = now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  
  const todoItems: BriefingTodoItem[] = [];
  const warnings: string[] = [];

  // ── 1. Harvest-ready bags
  harvestPredictions
    .filter(p => p.status === 'ready' || p.status === 'overdue')
    .forEach(p => {
      const species = getSpeciesById(p.speciesId);
      todoItems.push({
        emoji: species?.emoji ?? '🍄',
        bagName: p.bagName,
        action: p.status === 'overdue' ? 'ACİL hasat et' : 'Hasat et',
        reason: `${p.daysIn.toFixed(0)}. gün, ${p.status === 'overdue' ? 'gecikmiş' : 'ideal pencerede'}`,
        priority: p.status === 'overdue' ? 'high' : 'medium',
      });
    });

  // ── 2. Bags ready for fruiting transition
  bags
    .filter(b => b.status === 'incubation')
    .forEach(bag => {
      const speciesData = SPECIES_INCUBATION_DAYS[bag.speciesId];
      if (!speciesData) return;
      const daysIn = (Date.now() - new Date(bag.inoculationDate).getTime()) / 86400000;
      if (daysIn >= speciesData.typicalDays * 1.2) {
        warnings.push(`${bag.name} inkubasyon gecikmede (${Math.round(daysIn)}. gün, hedef ${speciesData.typicalDays})`);
      } else if (daysIn >= speciesData.typicalDays) {
        const species = getSpeciesById(bag.speciesId);
        todoItems.push({
          emoji: species?.emoji ?? '🌱',
          bagName: bag.name,
          action: 'Meyvelemeye al',
          reason: `${Math.round(daysIn)}. gün, hazır`,
          priority: 'medium',
        });
      }
    });

  // ── 3. Contamination suspects
  contaminationRisks
    .filter(r => r.riskLevel === 'high' || r.riskLevel === 'critical')
    .forEach(r => {
      todoItems.push({
        emoji: '🔬',
        bagName: r.bagName,
        action: r.riskLevel === 'critical' ? 'ACİL muayene + izole et' : 'Muayene et',
        reason: `Risk skoru: ${r.riskScore}/100`,
        priority: r.riskLevel === 'critical' ? 'high' : 'medium',
      });
    });

  // ── 4. Bags needing flush recording
  bags
    .filter(b => b.status === 'harvested' && b.flushHarvests.length > 0)
    .forEach(bag => {
      const lastFlush = bag.flushHarvests[bag.flushHarvests.length - 1];
      if (!lastFlush) return;
      const daysSinceFlush = (Date.now() - new Date(lastFlush.harvestDate).getTime()) / 86400000;
      const window = SPECIES_FRUITING_WINDOW[bag.speciesId];
      if (window && daysSinceFlush >= window.minDays && bag.flushCount < 3) {
        const species = getSpeciesById(bag.speciesId);
        todoItems.push({
          emoji: species?.emoji ?? '🔄',
          bagName: bag.name,
          action: `${bag.flushCount + 1}. flush başlattıysan kayıt et`,
          reason: `Son hasat ${Math.round(daysSinceFlush)} gün önce`,
          priority: 'low',
        });
      }
    });

  // ── 5. Soon-to-harvest bags (upcoming 2 days)
  harvestPredictions
    .filter(p => p.status === 'soon')
    .forEach(p => {
      const species = getSpeciesById(p.speciesId);
      todoItems.push({
        emoji: species?.emoji ?? '⏳',
        bagName: p.bagName,
        action: 'Hazırlık yap — yarın hasat',
        reason: `${p.daysLeft} gün kaldı`,
        priority: 'low',
      });
    });

  // ── Warnings
  // Humidity drops overnight
  if (yesterdayReadings.length > 0) {
    const nightReadings = yesterdayReadings.filter(r => {
      const h = new Date(r.timestamp).getHours();
      return h >= 22 || h < 6;
    });
    const lowHumCount = nightReadings.filter(r => r.humidity < 80).length;
    if (lowHumCount >= 3) {
      warnings.push(`Çadır nem: Dün gece ${lowHumCount} kez %80'in altına düştü`);
    }
  }

  // Critical alerts from yesterday
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const yesterdayAlerts = alerts.filter(a => a.timestamp.startsWith(yesterday) && a.severity === 'critical');
  if (yesterdayAlerts.length > 0) {
    warnings.push(`Dün ${yesterdayAlerts.length} kritik uyarı oluştu`);
  }

  // ── Yesterday summary
  const yesterdayBags = bags.filter(b => {
    if (!b.harvestDate) return false;
    return b.harvestDate.startsWith(yesterday);
  });
  const harvestGrams = yesterdayBags.reduce((s, b) => s + b.totalYieldGrams, 0);
  const activeBagCount = bags.filter(b => b.status === 'incubation' || b.status === 'fruiting').length;

  // Device statuses
  const deviceStatuses = devices.map(d => ({
    name: d.name,
    ok: d.isOnline,
  }));

  // Sort todos: high first, then medium, then low
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  todoItems.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return {
    date,
    todoItems,
    warnings,
    yesterdaySummary: {
      harvestGrams,
      activeBagCount,
      lostBagCount: 0, // Could track from loss records
      healthScore,
    },
    deviceStatuses,
  };
}

// ── Telegram Formatter ────────────────────────────────────────────────────────

export function formatMorningBriefingForTelegram(data: MorningBriefingData): string {
  const lines: string[] = [
    `🌅 <b>GÜNAYIN — ${data.date}</b>`,
    '',
  ];

  // Todo list
  if (data.todoItems.length > 0) {
    lines.push('📋 <b>BUGÜN YAPILACAKLAR:</b>');
    data.todoItems.forEach(item => {
      const urgency = item.priority === 'high' ? '❗' : '';
      lines.push(`• ${item.emoji} ${item.bagName} → ${urgency}${item.action} (${item.reason})`);
    });
    lines.push('');
  } else {
    lines.push('📋 <b>BUGÜN:</b> Planlanmış aksiyon yok — rutin kontrol yeterli ✅');
    lines.push('');
  }

  // Warnings
  if (data.warnings.length > 0) {
    lines.push('⚠️ <b>DİKKAT:</b>');
    data.warnings.forEach(w => lines.push(`• ${w}`));
    lines.push('');
  }

  // Yesterday summary
  const ys = data.yesterdaySummary;
  lines.push('📊 <b>DÜNÜN ÖZETİ:</b>');
  lines.push(`• Hasat: ${ys.harvestGrams}g | Aktif bag: ${ys.activeBagCount} | Kaybedilen: ${ys.lostBagCount}`);
  lines.push(`• Sağlık skoru: ${ys.healthScore}/100`);
  lines.push('');

  // Device status
  const deviceLine = data.deviceStatuses
    .map(d => `${d.name} ${d.ok ? '✅' : '❌'}`)
    .join(' | ');
  lines.push(`🔋 <b>CİHAZLAR:</b> ${deviceLine}`);

  return lines.join('\n');
}
